import { useToast } from "@/hooks/use-toast";
import { callRpcList } from "@/integrations/supabase/rpc";
import { devLog } from "@/lib/logger";
import { DashboardData, DashboardState } from "@/types/dashboard";
import { useEffect, useMemo, useState } from "react";

export type { DashboardState };

// Frontend Data Canon (CANON v1.3.0 section 4.3)
function mapKindToType(kind: string): string {
  const map: Record<string, string> = {
    'drain': 'Liquidation',
    'va_deposit': 'Deposit',
    'payout': 'Withdrawal',
    'transfer': 'Transfer',
    'return': 'Return'
  };
  return map[kind] || kind;
}

function mapKindToDirection(kind: string): 'deposit' | 'withdrawal' | 'transfer' {
  if (kind === 'va_deposit' || kind === 'return') return 'deposit';
  if (kind === 'payout' || kind === 'drain') return 'withdrawal';
  return 'transfer';
}

export const useDashboardState = (customerId: string) => {
  const { toast } = useToast();
  const [data, setData] = useState<DashboardData>({
    payoutRoutes: [],
    addresses: [],
    transactions: [],
    loading: true,
    error: null
  });
  const [virtualAccountsCount, setVirtualAccountsCount] = useState(0);
  const [externalAccountsCount, setExternalAccountsCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = async () => {
    // E2E Testing Bypass
    if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      setData({
        payoutRoutes: [],
        addresses: [],
        transactions: [],
        loading: false,
        error: null
      });
      setVirtualAccountsCount(0);
      setExternalAccountsCount(0);
      return;
    }

    if (!customerId) {
      setVirtualAccountsCount(0);
      setExternalAccountsCount(0);
      return;
    }
    
    setData(prev => ({ ...prev, loading: true }));
    
    try {
      // Use new user-scoped RPC functions instead of direct .from() calls
      const routesResult = await callRpcList('user_list_payout_routes', { _limit: 50, _offset: 0 });
      const addressesResult = await callRpcList('user_list_liquidation_addresses');
      const transactionsResult = await callRpcList('user_list_transactions', { _limit: 10 });
      // Also check for Virtual Accounts and External Accounts (Bank Accounts)
      const virtualAccountsResult = await callRpcList('user_virtual_accounts_list').catch(() => null);
      const externalAccountsResult = await callRpcList('user_list_external_accounts').catch(() => null);

      // ✅ NULL-SAFE: Validate results before accessing .data
      devLog('[useDashboardState] RPC results:', {
        routesResult: { isObject: typeof routesResult === 'object', hasData: !!routesResult?.data, isArray: Array.isArray(routesResult?.data) },
        addressesResult: { isObject: typeof addressesResult === 'object', hasData: !!addressesResult?.data, isArray: Array.isArray(addressesResult?.data) },
        transactionsResult: { isObject: typeof transactionsResult === 'object', hasData: !!transactionsResult?.data, isArray: Array.isArray(transactionsResult?.data) },
        virtualAccountsResult: { exists: !!virtualAccountsResult, hasData: !!virtualAccountsResult?.data },
        externalAccountsResult: { exists: !!externalAccountsResult, hasData: !!externalAccountsResult?.data }
      });
      
      const routesData = (routesResult && typeof routesResult === 'object' && Array.isArray(routesResult.data)) 
        ? routesResult.data 
        : [];
      const addressesData = (addressesResult && typeof addressesResult === 'object' && Array.isArray(addressesResult.data))
        ? addressesResult.data
        : [];
      const transactionsData = (transactionsResult && typeof transactionsResult === 'object' && Array.isArray(transactionsResult.data))
        ? transactionsResult.data
        : [];
      
      // Extract Virtual Accounts and External Accounts (Bank Accounts)
      let virtualAccountsData: any[] = [];
      if (virtualAccountsResult && typeof virtualAccountsResult === 'object') {
        const vaData = (virtualAccountsResult as any).data;
        if (Array.isArray(vaData)) {
          virtualAccountsData = vaData;
        } else if (vaData && typeof vaData === 'object' && Array.isArray((vaData as any).data)) {
          virtualAccountsData = (vaData as any).data;
        }
      }
      
      let externalAccountsData: any[] = [];
      if (externalAccountsResult && typeof externalAccountsResult === 'object') {
        const eaData = (externalAccountsResult as any).data;
        if (Array.isArray(eaData)) {
          externalAccountsData = eaData;
        } else if (eaData && typeof eaData === 'object' && Array.isArray((eaData as any).data)) {
          externalAccountsData = (eaData as any).data;
        }
      }
      
      devLog('[useDashboardState] Extracted data:', {
        routesCount: routesData.length,
        addressesCount: addressesData.length,
        transactionsCount: transactionsData.length,
        virtualAccountsCount: virtualAccountsData.length,
        externalAccountsCount: externalAccountsData.length
      });
      
      // Store counts for state determination
      setVirtualAccountsCount(virtualAccountsData.length);
      setExternalAccountsCount(externalAccountsData.length);

      setData({
        payoutRoutes: (routesData || []).map((route: any) => ({
          ...route,
          id: route.id,
          user_id: route.customer_id, // Map for compatibility
          is_default: false,
          bank_name: route.bank_name || '',
          bank_account_last4: route.last_4 || '',
          country: null,
          rail: null,
          bridge_customer_id: null,
          source_asset: null,
          source_chain: null,
          created_at: route.created_at,
          updated_at: route.updated_at
        })),
        addresses: (addressesData || []).map((addr: any) => ({
          ...addr,
          id: addr.id,
          user_id: addr.customer_id, // Map for compatibility  
          payout_route_id: null,
          bridge_customer_id: null,
          created_at: addr.created_at,
          updated_at: addr.updated_at
        })),
        transactions: (transactionsData || []).map((tx: any) => ({
          // Core DB fields (CANON v1.3.0 section 4.3)
          id: tx.id,
          user_id: tx.user_id,
          customer_id: tx.customer_id,
          kind: tx.kind,
          status: tx.status || 'pending',
          amount_in: tx.amount_in,
          asset_in: tx.asset_in,
          amount_out: tx.amount_out,
          asset_out: tx.asset_out,
          fee: tx.fee,
          bridge_ids: tx.bridge_ids,
          liquidation_address_id: tx.liquidation_address_id,
          external_account_id: tx.external_account_id,
          settled_at: tx.settled_at,
          created_at: tx.created_at,
          updated_at: tx.updated_at,
          
          // Computed display fields (Frontend Data Canon)
          display_type: mapKindToType(tx.kind),
          display_amount: tx.amount_out || tx.amount_in || 0,
          display_currency: tx.asset_out || tx.asset_in || 'USD',
          direction: mapKindToDirection(tx.kind),
          occurred_at: tx.settled_at || tx.created_at
        })),
        loading: false,
        error: null
      });
    } catch (error) {
      // Dashboard data loading failed
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to load dashboard data",
        description: "Please try refreshing the page or check your connection.",
        variant: "destructive",
      });
      setData(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage 
      }));
      // Reset counts on error
      setVirtualAccountsCount(0);
      setExternalAccountsCount(0);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId, refreshKey]);

  const dashboardState: DashboardState = useMemo(() => {
    if (data.loading) return "LOADING";
    if (data.error) return "EMPTY"; // Show empty state on error for better UX
    
    // ✅ CRITICAL FIX: Check if user has ANY activity (addresses, transactions, VA, or bank accounts)
    // If user has addresses OR transactions OR virtual accounts OR external accounts, they should see ACTIVE dashboard
    const hasAddresses = data.addresses.length > 0;
    const hasTransactions = data.transactions.length > 0;
    const hasVirtualAccounts = virtualAccountsCount > 0;
    const hasExternalAccounts = externalAccountsCount > 0;
    
    // If user has ANY activity → ACTIVE (they've used the platform)
    if (hasAddresses || hasTransactions || hasVirtualAccounts || hasExternalAccounts) {
      return "ACTIVE";
    }
    
    // If user has payout routes but no addresses → BANK_ADDED (onboarding step)
    if (data.payoutRoutes.length > 0 && !hasAddresses) {
      return "BANK_ADDED";
    }
    
    // If user has addresses but no transactions → ADDRESS_CREATED (waiting for first transaction)
    if (hasAddresses && !hasTransactions) {
      return "ADDRESS_CREATED";
    }
    
    // Default: EMPTY (no activity yet)
    return "EMPTY";
  }, [data, virtualAccountsCount, externalAccountsCount]);

  const refresh = () => setRefreshKey(k => k + 1);

  return {
    dashboardState,
    data,
    refresh,
    loading: data.loading,
    virtualAccountsCount,
    externalAccountsCount
  };
};